
import { useCollection, useIO, useLinked, useModel } from "@type-r/react";
import { getParamsFromPath, Loading, PageLayout, TilesList } from "lqx-react";
import * as React from "react";
import { CarrierType, ExpiredQuote, localUserInfo } from "api";
import { OrderModel, OrderCount, OrderFilters } from "../../../../api/order";
import { CategoryTab } from "./common";
import { OrderDetails } from "./details";
import { OrdersHeader } from "./header";
import { OrdersGrid } from "./orders-grid";
import { ExpiredQuoteTile, OrderTile } from "./orders-list";
import { SearchToolbar } from "./toolbar";

const defaultSort = {
  attr: "created_on_utc",
  asc: false
};

// Orders management page
export const OrdersPage = () => {
  const { isAdminRead, isSupplier } = localUserInfo;

  const orders = useCollection.of(OrderModel);
  orders.getEndpoint = OrderModel.getFeatureEnabledEndpoint;

  const orderCounts = useCollection.of(OrderCount);
  orderCounts.getEndpoint = OrderCount.getFeatureEnabledEndpoint;

  const expiredQuotes = useCollection.of(ExpiredQuote);
  expiredQuotes.getEndpoint = ExpiredQuote.getFeatureEnabledEndpoint;

  const carrierTypes = useCollection.of(CarrierType);
  const selected = useCollection.subsetOf(orders);

  const orderFilter = useModel(OrderFilters);
  const isFilterReady = useIO(async () => orderFilter.initializeOptions());

  const $category = useLinked<CategoryTab>("all"),
    category = $category.value,
    $sort = useLinked(defaultSort),
    sort = $sort.value,
    $isGrid = useLinked(false),
    $expandedAppBar = useLinked(false);

  let [orderId] = getParamsFromPath();
  
  useIO(async () => {
    //const result = await CarrierType.getAll();
    //carrierTypes.set(result, { parse: true });
  });
  
  const isOrdersView = category !== "expired";
  const items = isOrdersView ? orders : expiredQuotes;

  const isReady = useIO(
    async () => {
      if (orderId || !isFilterReady) return;

      if ($category.value == 'expired' && (isSupplier || isAdminRead)) {
        if (!expiredQuotes.hasPendingIO())
          await expiredQuotes.fetch()
        return;
      }

      if($category.value !== 'expired' && orders.length){
        await fetchOrders();
        return;
      }

      await Promise.all([
        orderCounts.fetch(),
        fetchOrders(),
        (isSupplier || isAdminRead) && expiredQuotes.fetch()
      ]);
    },
    // Load data every time the category changes.
    [category, orderId, sort, orderFilter["_changeToken"], isFilterReady]
  );

  const fetchOrders = () =>{
    return orders.fetch({
      params: {
        searchString: orderFilter.filterAll && orderFilter.filterAll.length > 2 ? orderFilter.filterAll : "",
        orderStatuses: (orderFilter.orderStatuses && orderFilter.orderStatuses.selected) ? Array.from(new Set(orderFilter.orderStatuses.selected)) : null,
        partial: true,
        sortBy: sort.attr,
        asc: sort.asc,
        ...(category !== "all" && { category: category, searchString: orderFilter.filterAll })
      }
    })
  }
  


  // Reset sorting if switched between quotes and orders
  React.useEffect(() => {
    const { sortBy } = items.model as any;
    $sort.at("attr").set(Object.keys(sortBy)[0]);
  }, [isOrdersView]);


  return !orderId ? (
    <PageLayout
      maxWidth={false}
      header={
        <OrdersHeader
          $category={$category}
          orderCounts={orderCounts}
          expiredQuotes={expiredQuotes}
        />
      }
      subheader={
        <SearchToolbar
          sortOptions={(items.model as any).sortBy}
          omFilter={orderFilter}
          $sort={$sort}
          $isGrid={$isGrid}
          download={isOrdersView ? (items.models as OrderModel[]) : null}
        />
      }
      footer={undefined}
    >
      {isReady && isFilterReady ? (
        $isGrid.value ? (
          <OrdersGrid items={items.models} isOrdersView={isOrdersView} />
        ) : (
            <TilesList
              expandedAppBar={$expandedAppBar.value}
              items={items.models}
              itemSize={60}
              renderer={isOrdersView ? OrderTile : ExpiredQuoteTile}
              selected={undefined /* selected */}
            />
          )
      ) : (
          <Loading />
        )}
    </PageLayout>
  ) : (
      <OrderDetails orderId={orderId} carrierTypes = {carrierTypes} />
    );
};
